import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, SectionCard } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { useAuthContext } from '@/providers/AuthProvider';
import {
  productCollectionRawQueryKey,
  useProductCollectionInputsQuery,
  useProductCollectionRawQuery,
} from '../hooks/useProductCollectionInputs';
import { RAW_VIEW_ROLES, fetchRawCollectionInputs, hasAnyRole } from '../lib/piiAccess';

interface ProductCollectionInputsSectionProps {
  orderId: string | undefined;
  /**
   * QA-302 — 주문상품 표에 품목별로 이미 보여 준 상품코드. 이 그룹은 섹션 목록에서 빼고(중복 방지), 섹션은
   * 원문보기 버튼과 품목에 붙지 못한 그룹만 보인다.
   */
  inlineProductCodes?: ReadonlySet<string>;
}

/**
 * 항목9(b) — BO 주문상세 "상품 추가입력" 섹션(read-only).
 * be-catalog `GET /admin/v1/product-collections/by-order/{orderId}` 소비.
 *
 * 통관(OrderFormFieldValuesSection)·이벤트 응모(EventApplicationInputsSection) 와 별개 레이어
 * (상품별 추가입력)를 별도 카드로 노출한다. 응답은 상품(productCode/productName)별 그룹이며,
 * 그룹 헤더(상품명 + 자체상품코드) 아래 항목명(label) → 값(마스킹 표시값 그대로)을 나열한다.
 * 내부 fieldId/fieldKey 와 productId(UUID) 는 노출하지 않는다(화면 비노출 규칙).
 *
 * 입력이 하나도 없는 주문은 섹션 자체를 숨긴다. 로딩/에러/빈 값이면 비노출 — read-only
 * 부가정보라 404·권한 등 에러에도 본문을 막지 않고 조용히 숨긴다(통관 섹션과 동일 정책).
 * 값 가공/마스킹은 BE 권위(FE 는 표시만).
 *
 * 원문보기(be-catalog b662243) — 가려진 항목이 있고 권한(SUPER_ADMIN·OPS_ADMIN·CS_AGENT)이 있으면 버튼을 보인다.
 * 누를 때마다 BE 감사로그가 1건 남으므로, 확인을 받고 그때만 `/raw` 를 부른다(자동 호출 없음). 받은 원문은
 * 메모리 쿼리 캐시(productCollectionRawQueryKey)에만 두어 주문상품 표 인라인과 함께 쓰고(QA-302), "다시 가리기"·
 * 주문 변경·화면 이탈(섹션 언마운트) 때 지운다. 저장소에는 넣지 않는다.
 */
export const ProductCollectionInputsSection = ({
  orderId,
  inlineProductCodes,
}: ProductCollectionInputsSectionProps) => {
  const { t } = useTranslation('orders');
  const query = useProductCollectionInputsQuery(orderId);
  const { currentAdmin } = useAuthContext();
  const canViewRaw = hasAnyRole(currentAdmin?.roles ?? [], RAW_VIEW_ROLES);

  const qc = useQueryClient();
  const raw = useProductCollectionRawQuery(orderId).data ?? null;
  const setRaw = (v: typeof raw) => {
    if (!orderId) return;
    // 다시 가리기 = null(관찰 중인 화면이 즉시 가린 값으로 돌아오게). 키 제거는 화면 이탈 때만.
    qc.setQueryData(productCollectionRawQueryKey(orderId), v);
  };
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);

  // 다른 주문으로 바뀌거나 화면을 떠나면 이전 주문의 원문을 즉시 버린다.
  useEffect(() => {
    setRawError(null);
    return () => {
      if (orderId) qc.removeQueries({ queryKey: productCollectionRawQueryKey(orderId) });
    };
  }, [orderId, qc]);

  const masked = (query.data?.products ?? []).filter((g) => g.inputs.length > 0);
  const allGroups = raw ? raw.filter((g) => g.inputs.length > 0) : masked;
  const groups = inlineProductCodes ? allGroups.filter((g) => !inlineProductCodes.has(g.productCode)) : allGroups;
  if (query.isPending || query.isError || masked.length === 0) return null;
  // 전부 주문상품 표에 붙었고 원문보기도 필요 없으면 카드 자체를 숨긴다.
  const hasMaskedAny = masked.some((g) => g.inputs.some((i) => i.masked));
  if (groups.length === 0 && !hasMaskedAny) return null;

  const hasMasked = masked.some((g) => g.inputs.some((i) => i.masked));

  const revealRaw = async () => {
    if (!orderId) return;
    const ok = window.confirm(
      t('orders.detail.rawViewConfirm', '개인정보 원문을 조회합니다. 조회 기록(감사로그)이 남습니다. 계속하시겠습니까?'),
    );
    if (!ok) return;
    setLoadingRaw(true);
    setRawError(null);
    try {
      const data = await fetchRawCollectionInputs(orderId);
      setRaw(data.products);
    } catch (e) {
      setRawError(e instanceof Error ? e.message : t('orders.detail.rawViewFailed', '원문을 불러오지 못했습니다.'));
    } finally {
      setLoadingRaw(false);
    }
  };

  const action = !canViewRaw || !hasMasked ? null : raw ? (
    <Button type="button" variant="outline" size="sm" onClick={() => setRaw(null)} data-testid="collection-raw-hide">
      {t('orders.detail.rawViewHide', '다시 가리기')}
    </Button>
  ) : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => void revealRaw()}
      disabled={loadingRaw}
      data-testid="collection-raw-view"
    >
      {loadingRaw ? t('orders.detail.rawViewLoading', '불러오는 중…') : t('orders.detail.rawView', '원문보기')}
    </Button>
  );

  return (
    <SectionCard
      title={t('orders.detail.productCollectionInputs', '상품 추가입력')}
      aside={action}
      data-testid="product-collection-inputs"
    >
      {raw ? (
        <p
          className="mb-3 rounded px-2 py-1 text-[12px]"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
          data-testid="collection-raw-notice"
        >
          {t('orders.detail.rawViewNotice', '개인정보 원문을 표시 중입니다. 조회 기록이 남았습니다. 화면을 떠나면 다시 가려집니다.')}
        </p>
      ) : null}
      {rawError ? (
        <p
          role="alert"
          className="mb-3 rounded px-2 py-1 text-[12px]"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {rawError}
        </p>
      ) : null}
      {groups.length === 0 ? (
        <p className="text-[12px] text-[color:var(--content-muted)]" data-testid="collection-inline-note">
          {t('orders.detail.collectionInlineNote', '입력값은 위 주문상품 표의 품목별로 함께 표시됩니다. 원문은 [원문보기]로 확인하세요.')}
        </p>
      ) : null}
      <div className="space-y-4" data-sentry-mask>
        {groups.map((group, gIdx) => (
          <div key={`${group.productCode || group.productName}-${gIdx}`}>
            {/* 상품 그룹 헤더 — 상품명 + 상품코드 · 자체상품코드(내부 UUID 미노출). */}
            <p className="mb-1 text-sm font-semibold text-[color:var(--content-strong)]">
              {group.productName || '-'}
              {group.productCode || group.selfProductCode ? (
                <span
                  className="ml-1 text-[11px] font-normal text-[color:var(--content-muted)]"
                  data-testid="collection-group-codes"
                >
                  (
                  {[
                    group.productCode,
                    group.selfProductCode
                      ? `${t('orders.detail.selfProductCode', '자체상품코드')} ${group.selfProductCode}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  )
                </span>
              ) : null}
            </p>
            <ul className="space-y-2">
              {group.inputs.map((input, idx) => (
                <li
                  key={`${input.label}-${idx}`}
                  className="grid grid-cols-[140px_1fr] items-baseline gap-3 text-sm"
                >
                  <span className="text-[color:var(--content-muted)]">{input.label}</span>
                  <span className="flex items-center gap-2 text-[color:var(--content-strong)]">
                    <span>{input.value || '-'}</span>
                    {input.masked ? (
                      <span
                        className="rounded px-1.5 py-0.5 text-[11px]"
                        style={{
                          background: 'var(--status-neutral-bg)',
                          color: 'var(--status-neutral-fg)',
                        }}
                        title={t('orders.detail.maskedTitle', '개인정보 보호를 위해 마스킹된 값입니다.')}
                      >
                        {t('orders.detail.maskedBadge', '마스킹')}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};
